import { daysPerYear, festivalsOn, fromAbsoluteDay, moonPhaseFraction, toAbsoluteDay, weekdayName, type CalendarSystem, type Festival } from '@realmery/realm-core';

/**
 * A calendar's month as a grid of weeks (docs/CHRONOLOGY-CALENDAR-REDESIGN-PLAN.md slice 3),
 * and what each day carries: new and full moons, festivals, seasons starting and ending.
 */

/** A month of a year; `monthIndex` is 0-based. */
export interface MonthOf {
  readonly year: number;
  readonly monthIndex: number;
}

export interface MonthGrid {
  /** The weekday names, in the week's order. */
  readonly weekdays: readonly string[];
  /** Each week's days as absolute days; null before the month's first day and after its last. */
  readonly weeks: readonly (readonly (number | null)[])[];
  readonly first: number;
  readonly last: number;
}

export function monthGrid(calendar: CalendarSystem, { year, monthIndex }: MonthOf): MonthGrid {
  const perWeek = Math.max(1, calendar.daysPerWeek);
  const weekdays = Array.from({ length: perWeek }, (_, i) => weekdayName(calendar, i) || `Day ${i + 1}`);
  const first = toAbsoluteDay(calendar, year, monthIndex, 1);
  const length = calendar.months[monthIndex]?.lengthInDays ?? 0;
  const lead = ((first % perWeek) + perWeek) % perWeek;
  const cells: (number | null)[] = [...Array<null>(lead).fill(null), ...Array.from({ length }, (_, i) => first + i)];
  while (cells.length % perWeek !== 0) cells.push(null);
  const weeks: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += perWeek) weeks.push(cells.slice(i, i + perWeek));
  return { weekdays, weeks, first, last: first + Math.max(0, length - 1) };
}

export function stepMonth(calendar: CalendarSystem, { year, monthIndex }: MonthOf, by: 1 | -1): MonthOf {
  const count = calendar.months.length;
  const next = monthIndex + by;
  if (next < 0) return { year: year - 1, monthIndex: count - 1 };
  if (next >= count) return { year: year + 1, monthIndex: 0 };
  return { year, monthIndex: next };
}

export function monthOfDay(calendar: CalendarSystem, day: number): MonthOf {
  const { year, monthIndex } = fromAbsoluteDay(calendar, day);
  return { year, monthIndex };
}

export interface DayMarks {
  /** A moon new or full on the day: the one day of its cycle nearest the exact phase. */
  readonly moons: readonly { readonly name: string; readonly phase: 'new' | 'full' }[];
  readonly festivals: readonly Festival[];
  readonly seasonsStarting: readonly string[];
  readonly seasonsEnding: readonly string[];
}

export function dayMarks(calendar: CalendarSystem, day: number): DayMarks {
  const moons = calendar.moons.flatMap((moon): DayMarks['moons'] => {
    if (!(moon.cycleLengthDays >= 2)) return [];
    const fraction = moonPhaseFraction(moon, day);
    const half = 0.5 / moon.cycleLengthDays;
    if (fraction < half || fraction >= 1 - half) return [{ name: moon.name, phase: 'new' as const }];
    if (fraction >= 0.5 - half && fraction < 0.5 + half) return [{ name: moon.name, phase: 'full' as const }];
    return [];
  });
  const date = fromAbsoluteDay(calendar, day);
  const on = (monthIndex: number, d: number) => monthIndex === date.monthIndex && d === date.day;
  return {
    moons,
    festivals: festivalsOn(calendar, day),
    seasonsStarting: calendar.seasons.filter((s) => on(s.startMonth, s.startDay)).map((s) => s.name),
    seasonsEnding: calendar.seasons.filter((s) => on(s.endMonth, s.endDay)).map((s) => s.name),
  };
}

/** Whether a calendar can be drawn: months with days, and a year that isn't empty. */
export function drawable(calendar: CalendarSystem): boolean {
  return calendar.months.length > 0 && daysPerYear(calendar) > 0;
}
